import { useState, useEffect, Fragment } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Search, Plus, ChevronRight, Trash2, Pencil, Paperclip, Link2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { useConfirm } from '@/components/ConfirmDialog';
import { SearchableSelect } from '@/components/SearchableSelect';
import StatusDot from '@/components/StatusDot';
import { expirationColor } from '@/lib/permitDate';
import { LinkExistingDialog } from '@/components/LinkExistingDialog';
import { inProject, buildProjectLinkPatch } from '@/lib/projectLink';

const PERMIT_TYPES = ['Fuel', 'Generator/Battery', 'HVAC/Heater', 'Explosives/Fireworks/Pyrotechnics', 'Open Flame', 'Portable Fire Extinguisher Tag', 'Other'];
const STATUSES = ['Active', 'Expired', 'Pending', 'Denied'];

const EMPTY_FORM = {
  property_id: '', project_id: '', permit_type: 'Fuel', permit_number: '',
  status: 'Active', issue_date: '', expiration_date: '', cost: '', notes: '',
};

const fmtMoney = (v) => (v === null || v === undefined || v === '') ? '\u00A0' : `$${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = (iso) => {
  if (!iso) return null;
  const d = new Date(`${String(iso).slice(0, 10)}T00:00:00`);
  return isNaN(d.getTime()) ? null : d.toLocaleDateString();
};

// FDNY operational permits - fully manual (no NYC Open Data source).
// scope is driven by props: lockedProjectId (Project Detail) > lockedPropertyId (Property Profile) > hub (all).
export default function FdnyPermits({ lockedPropertyId, lockedProjectId }) {
  const confirm = useConfirm();
  const navigate = useNavigate();
  const [permits, setPermits] = useState([]);
  const [properties, setProperties] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  const [statusFilter, setStatusFilter] = useState('All');
  const [search, setSearch] = useState('');

  const [addOpen, setAddOpen] = useState(false);
  const [addForm, setAddForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const [expandedId, setExpandedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [editForm, setEditForm] = useState(EMPTY_FORM);
  const [docCounts, setDocCounts] = useState({});

  // Link Existing (project scope only)
  const [linkOpen, setLinkOpen] = useState(false);
  const [linking, setLinking] = useState(false);
  const [linkCandidates, setLinkCandidates] = useState([]);

  const isProjectScope = !!lockedProjectId;
  const showPropertyCol = !lockedPropertyId && !lockedProjectId;

  const fetchPermits = () => {
    const params = {};
    if (lockedProjectId) params.project_id = lockedProjectId;
    else if (lockedPropertyId) params.property_id = lockedPropertyId;
    return api.get('/fdny-permits', { params }).then(r => setPermits(Array.isArray(r.data) ? r.data : [])).catch(() => setPermits([]));
  };

  useEffect(() => {
    setLoading(true);
    fetchPermits().finally(() => setLoading(false));
    api.get('/properties').then(r => setProperties(Array.isArray(r.data) ? r.data : [])).catch(() => setProperties([]));
    api.get('/projects').then(r => setProjects(Array.isArray(r.data) ? r.data : [])).catch(() => setProjects([]));
    api.get('/documents/link-counts', { params: { record_type: 'FDNY Permit' } }).then(r => setDocCounts(r.data || {})).catch(() => setDocCounts({}));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lockedPropertyId, lockedProjectId]);

  const lockedProp = (Array.isArray(properties) ? properties : []).find(p => p.id === lockedPropertyId);
  const defaultPropertyId = lockedPropertyId || '';

  const openAdd = () => {
    setAddForm({ ...EMPTY_FORM, property_id: defaultPropertyId, project_id: lockedProjectId || '' });
    setAddOpen(true);
  };

  // Fetch this property's FDNY permits not already on this project, then open the picker.
  const openLink = async () => {
    setLinkOpen(true);
    try {
      const r = await api.get('/fdny-permits', { params: { property_id: lockedPropertyId } });
      const all = Array.isArray(r.data) ? r.data : [];
      setLinkCandidates(all.filter(p => !inProject(p, lockedProjectId)));
    } catch { setLinkCandidates([]); }
  };

  const linkSelected = async (ids) => {
    setLinking(true);
    try {
      await Promise.all(ids.map(id => {
        const p = linkCandidates.find(x => x.id === id);
        return api.put(`/fdny-permits/${id}`, buildProjectLinkPatch(p, lockedProjectId));
      }));
      toast.success(`Linked ${ids.length} permit${ids.length === 1 ? '' : 's'} to this project`);
      setLinkOpen(false);
      fetchPermits();
    } catch (e) { toast.error('Could not link some permits'); }
    setLinking(false);
  };

  const createPermit = async () => {
    if (!addForm.property_id) return toast.error('Property is required');
    setSaving(true);
    try {
      await api.post('/fdny-permits', { ...addForm, project_id: addForm.project_id || null, cost: addForm.cost === '' ? null : addForm.cost });
      toast.success('FDNY permit added');
      setAddOpen(false);
      setAddForm(EMPTY_FORM);
      fetchPermits();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error adding permit'); }
    setSaving(false);
  };

  const toggleRow = (p) => {
    if (expandedId === p.id) { setExpandedId(null); setDetail(null); setEditMode(false); return; }
    setExpandedId(p.id);
    setDetail(p);
    setEditMode(false);
  };

  const startEdit = () => {
    setEditForm({
      property_id: detail.property_id || '', project_id: detail.project_id || '',
      permit_type: detail.permit_type || 'Other', permit_number: detail.permit_number || '',
      status: detail.status || 'Active', issue_date: (detail.issue_date || '').slice(0, 10),
      expiration_date: (detail.expiration_date || '').slice(0, 10),
      cost: detail.cost === null || detail.cost === undefined ? '' : detail.cost, notes: detail.notes || '',
    });
    setEditMode(true);
  };

  const saveEdit = async () => {
    setSaving(true);
    try {
      const r = await api.put(`/fdny-permits/${detail.id}`, { ...editForm, project_id: editForm.project_id || null, cost: editForm.cost === '' ? null : editForm.cost });
      setDetail(r.data);
      setPermits(prev => prev.map(x => (x.id === r.data.id ? r.data : x)));
      setEditMode(false);
      toast.success('Permit updated');
    } catch (e) { toast.error(e.response?.data?.detail || 'Error updating permit'); }
    setSaving(false);
  };

  const deletePermit = async (id) => {
    const ok = await confirm({ title: 'Delete FDNY permit', description: 'Delete this permit? This cannot be undone.', confirmLabel: 'Delete', destructive: true });
    if (!ok) return;
    try {
      await api.delete(`/fdny-permits/${id}`);
      toast.success('Permit deleted');
      setExpandedId(null);
      setDetail(null);
      fetchPermits();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error deleting permit'); }
  };

  const filtered = (Array.isArray(permits) ? permits : []).filter(p => {
    if (statusFilter !== 'All' && p.status !== statusFilter) return false;
    const q = search.toLowerCase();
    if (q && !(
      (p.permit_type || '').toLowerCase().includes(q) ||
      (p.permit_number || '').toLowerCase().includes(q) ||
      (p.property_display_name || '').toLowerCase().includes(q) ||
      (p.property_address || '').toLowerCase().includes(q)
    )) return false;
    return true;
  });

  const renderFields = (form, setForm, prefix) => {
    const propertyOpts = (Array.isArray(properties) ? properties : []).map(p => ({ value: p.id, label: `${p.display_name || p.address}${p.borough ? `: ${p.borough}` : ''}` }));
    const projectOpts = (Array.isArray(projects) ? projects : []).filter(p => form.property_id && p.property_id === form.property_id).map(p => ({ value: p.id, label: `${p.project_number}: ${p.name}` }));
    return (
      <div className="space-y-3">
        {lockedPropertyId ? (
          <div>
            <Label className="text-xs text-muted-foreground">Property</Label>
            <Input value={lockedProp ? (lockedProp.display_name || lockedProp.address) : 'This property'} disabled className="bg-secondary/40 border-border" data-testid={`${prefix}-property-locked`} />
          </div>
        ) : (
          <div>
            <Label className="text-xs text-muted-foreground">Property *</Label>
            <SearchableSelect options={propertyOpts} value={form.property_id} onChange={(val) => setForm({ ...form, property_id: val, project_id: '' })} placeholder="Select property" searchPlaceholder="Search properties..." emptyText="No properties found." allowClear={false} testid={`${prefix}-property`} />
          </div>
        )}
        {!lockedProjectId && (
          <div>
            <Label className="text-xs text-muted-foreground">Project <span className="text-muted-foreground/60">(optional)</span></Label>
            <SearchableSelect options={projectOpts} value={form.project_id} onChange={(val) => setForm({ ...form, project_id: val })} placeholder={form.property_id ? 'Link a project (optional)' : 'Select a property first'} searchPlaceholder="Search projects..." emptyText={form.property_id ? 'No projects linked to this property.' : 'Select a property first.'} testid={`${prefix}-project`} />
          </div>
        )}
        <div className="grid grid-cols-2 gap-2">
          <div>
            <Label className="text-xs text-muted-foreground">Permit Type *</Label>
            <Select value={form.permit_type} onValueChange={v => setForm({ ...form, permit_type: v })}>
              <SelectTrigger className="bg-background border-border" data-testid={`${prefix}-type`}><SelectValue /></SelectTrigger>
              <SelectContent className="bg-card border-border">{PERMIT_TYPES.map(t => <SelectItem key={t} value={t} className="text-sm">{t}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs text-muted-foreground">Status</Label>
            <Select value={form.status} onValueChange={v => setForm({ ...form, status: v })}>
              <SelectTrigger className="bg-background border-border" data-testid={`${prefix}-status`}><SelectValue /></SelectTrigger>
              <SelectContent className="bg-card border-border">{STATUSES.map(s => <SelectItem key={s} value={s} className="text-sm">{s}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>
        <div>
          <Label className="text-xs text-muted-foreground">Permit Number</Label>
          <Input value={form.permit_number} onChange={e => setForm({ ...form, permit_number: e.target.value })} placeholder="Manual entry (format varies)" className="bg-background border-border" style={{ fontFamily: "'DM Mono', monospace" }} data-testid={`${prefix}-permit-number`} />
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <Label className="text-xs text-muted-foreground">Issue Date</Label>
            <Input type="date" value={form.issue_date || ''} onChange={e => setForm({ ...form, issue_date: e.target.value })} className="bg-background border-border" data-testid={`${prefix}-issue-date`} />
          </div>
          <div>
            <Label className="text-xs text-muted-foreground">Expiration Date</Label>
            <Input type="date" value={form.expiration_date || ''} onChange={e => setForm({ ...form, expiration_date: e.target.value })} className="bg-background border-border" data-testid={`${prefix}-expiration-date`} />
          </div>
        </div>
        <div>
          <Label className="text-xs text-muted-foreground">Cost ($) <span className="text-muted-foreground/60">(optional)</span></Label>
          <Input type="number" min="0" step="0.01" value={form.cost} onChange={e => setForm({ ...form, cost: e.target.value })} className="bg-background border-border" data-testid={`${prefix}-cost`} />
        </div>
        <div>
          <Label className="text-xs text-muted-foreground">Notes</Label>
          <Textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} rows={2} className="bg-background border-border" data-testid={`${prefix}-notes`} />
        </div>
      </div>
    );
  };

  const renderDetailPanel = () => {
    if (!detail) return null;
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-2 flex-wrap">
          {!editMode && <Button size="sm" variant="outline" className="border-border" onClick={startEdit} data-testid="edit-fdny-permit-btn"><Pencil className="h-3.5 w-3.5 mr-1" /> Edit</Button>}
          <Button size="sm" variant="outline" className="text-destructive hover:text-destructive border-border" onClick={() => deletePermit(detail.id)} data-testid="delete-fdny-permit-btn"><Trash2 className="h-3.5 w-3.5 mr-1" /> Delete</Button>
        </div>
        {editMode ? (
          <>
            {renderFields(editForm, setEditForm, 'edit-fdny')}
            <div className="flex gap-2 mt-2">
              <Button onClick={saveEdit} disabled={saving} className="bg-primary text-primary-foreground" data-testid="save-fdny-permit-btn">{saving ? 'Saving...' : 'Save Changes'}</Button>
              <Button variant="outline" onClick={() => setEditMode(false)}>Cancel</Button>
            </div>
          </>
        ) : (
          <div className="space-y-4" data-testid="fdny-permit-detail-view">
            <div className="grid grid-cols-2 gap-x-6 gap-y-2">
              {[
                ['Permit Type', detail.permit_type, false],
                ['Permit No.', detail.permit_number, true],
                ['Status', detail.status, false],
                ['Cost', fmtMoney(detail.cost), true],
                ['Issue Date', detail.issue_date, true],
              ].map(([label, val, mono]) => (
                <p key={label} className="text-sm">
                  <span style={{ color: '#031333' }}>{label}: </span>
                  {label === 'Status'
                    ? <StatusDot status={detail.status} />
                    : <span style={{ color: '#010A21', fontFamily: mono ? "'DM Mono', monospace" : undefined }}>{val || '\u00A0'}</span>}
                </p>
              ))}
              <p className="text-sm">
                <span style={{ color: '#031333' }}>Expiration Date: </span>
                <span style={{ fontFamily: "'DM Mono', monospace", color: expirationColor(detail.expiration_date) || '#010A21' }}>{detail.expiration_date || '\u00A0'}</span>
              </p>
              {showPropertyCol && (
                <p className="text-sm">
                  <span style={{ color: '#031333' }}>Property: </span>
                  {detail.property_id
                    ? <button onClick={() => navigate(`/properties/${detail.property_id}`)} className="text-[#1B4A76] hover:underline" data-testid="fdny-permit-detail-property-link">{detail.property_display_name || detail.property_address || 'View property'}</button>
                    : <span style={{ color: '#010A21' }}>-</span>}
                </p>
              )}
              <p className="text-sm">
                <span style={{ color: '#031333' }}>Project: </span>
                {detail.project_id
                  ? <button onClick={() => navigate(`/projects/${detail.project_id}`)} className="text-[#1B4A76] hover:underline" data-testid="fdny-permit-detail-project-link">View project</button>
                  : <span style={{ color: '#010A21' }}>-</span>}
              </p>
              <p className="text-sm">
                <span style={{ color: '#031333' }}>Documents: </span>
                {docCounts[detail.id] > 0
                  ? <button onClick={() => navigate(`/documents?record_type=${encodeURIComponent('FDNY Permit')}&record_id=${detail.id}`)} className="text-[#1B4A76] hover:underline inline-flex items-center gap-1" data-testid="fdny-permit-detail-doc-count"><Paperclip className="h-3 w-3" /> {docCounts[detail.id]} linked</button>
                  : <span style={{ color: '#010A21' }}>None</span>}
              </p>
            </div>
            {detail.notes && (
              <div>
                <p className="text-sm" style={{ color: '#031333' }}>Notes:</p>
                <p className="text-sm whitespace-pre-wrap" style={{ color: '#010A21' }}>{detail.notes}</p>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  const colCount = showPropertyCol ? 7 : 6;

  return (
    <div className="space-y-4" data-testid="fdny-permits-tab">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <p className="text-sm text-muted-foreground" data-testid="fdny-permits-summary">
            {filtered.filter(p => p.status === 'Active').length} active permit(s)
          </p>
          <p className="text-xs text-muted-foreground mt-0.5">Manual entry - FDNY operational permits are not on NYC Open Data.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button className="bg-primary text-primary-foreground" onClick={openAdd} data-testid="add-fdny-permit-btn">
            <Plus className="h-4 w-4 mr-1" /> Add Permit
          </Button>
          {lockedProjectId && lockedPropertyId && (
            <Button variant="outline" className="border-border" onClick={openLink} data-testid="link-existing-fdny-btn">
              <Link2 className="h-4 w-4 mr-1" /> Link Existing
            </Button>
          )}
        </div>
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-44 bg-card border-border text-sm" data-testid="fdny-permits-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-card border-border">
            <SelectItem value="All" className="text-sm">All Statuses</SelectItem>
            {STATUSES.map(s => <SelectItem key={s} value={s} className="text-sm">{s}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input placeholder="Search FDNY permits..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9 bg-card border-border" data-testid="fdny-permits-search" />
        </div>
      </div>

      <div className="bg-transparent">
        <Table>
          <TableHeader>
            <TableRow className="border-border hover:bg-transparent">
              <TableHead className="text-xs text-muted-foreground">Permit Type</TableHead>
              <TableHead className="text-xs text-muted-foreground">Permit Number</TableHead>
              {showPropertyCol && <TableHead className="text-xs text-muted-foreground">Property</TableHead>}
              <TableHead className="text-xs text-muted-foreground">Status</TableHead>
              <TableHead className="text-xs text-muted-foreground">Issue Date</TableHead>
              <TableHead className="text-xs text-muted-foreground">Expiration Date</TableHead>
              <TableHead className="text-xs text-muted-foreground text-right">Cost</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.map(p => (
              <Fragment key={p.id}>
                <TableRow className={`group border-border cursor-pointer ${expandedId === p.id ? 'bg-secondary/40' : 'hover:bg-secondary/30'}`} onClick={() => toggleRow(p)} data-testid={`fdny-permit-row-${p.id}`} style={{ height: '40px' }}>
                  <TableCell className="text-sm">
                    <span className="inline-flex items-center gap-1.5">
                      <ChevronRight className="h-3.5 w-3.5 text-muted-foreground transition-transform" style={{ transform: expandedId === p.id ? 'rotate(90deg)' : 'none' }} />
                      {p.permit_type}
                    </span>
                    {docCounts[p.id] > 0 && (
                      <button onClick={(e) => { e.stopPropagation(); navigate(`/documents?record_type=${encodeURIComponent('FDNY Permit')}&record_id=${p.id}`); }} className="mt-0.5 ml-5 flex items-center gap-1 text-xs text-[#1B4A76] hover:underline" data-testid={`fdny-permit-doc-count-${p.id}`}>
                        <Paperclip className="h-3 w-3" /> {docCounts[p.id]} document{docCounts[p.id] === 1 ? '' : 's'}
                      </button>
                    )}
                  </TableCell>
                  <TableCell className="text-sm" style={{ fontFamily: "'DM Mono', monospace" }}>{p.permit_number || '\u00A0'}</TableCell>
                  {showPropertyCol && (
                    <TableCell className="text-sm">
                      <button onClick={(e) => { e.stopPropagation(); navigate(`/properties/${p.property_id}`); }} className="text-[#1B4A76] hover:underline text-left" data-testid={`fdny-permit-property-link-${p.id}`}>
                        {p.property_display_name || p.property_address || '\u00A0'}
                      </button>
                    </TableCell>
                  )}
                  <TableCell><StatusDot status={p.status} /></TableCell>
                  <TableCell className="text-sm" style={{ fontFamily: "'DM Mono', monospace" }}>{p.issue_date || '\u00A0'}</TableCell>
                  <TableCell className="text-sm" style={{ fontFamily: "'DM Mono', monospace", color: expirationColor(p.expiration_date) }} data-testid={`fdny-permit-exp-${p.id}`}>{p.expiration_date || '\u00A0'}</TableCell>
                  <TableCell className="text-right text-sm" style={{ fontFamily: "'DM Mono', monospace" }}>{fmtMoney(p.cost)}</TableCell>
                </TableRow>
                <tr data-testid={`fdny-permit-expand-${p.id}`}>
                  <td colSpan={colCount} style={{ padding: 0, border: 0 }}>
                    <div style={{ maxHeight: expandedId === p.id ? '2000px' : '0', overflow: 'hidden', transition: 'max-height 200ms ease-out' }}>
                      {expandedId === p.id && (
                        <div className="px-4 py-4 bg-secondary/20 border-b border-border" data-testid={`fdny-permit-detail-${p.id}`}>
                          {renderDetailPanel()}
                        </div>
                      )}
                    </div>
                  </td>
                </tr>
              </Fragment>
            ))}
            {!loading && filtered.length === 0 && (
              <TableRow><TableCell colSpan={colCount} className="text-center text-sm text-muted-foreground py-8">No FDNY permits found. Click "Add Permit" to record one.</TableCell></TableRow>
            )}
            {loading && (
              <TableRow><TableCell colSpan={colCount} className="text-center text-sm text-muted-foreground py-8">Loading permits...</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <Sheet open={addOpen} onOpenChange={setAddOpen}>
        <SheetContent className="sm:max-w-md bg-card border-border overflow-y-auto" data-testid="add-fdny-permit-sheet">
          <SheetHeader>
            <SheetTitle>Add FDNY Permit</SheetTitle>
            <SheetDescription>Record an FDNY operational permit (manual entry).</SheetDescription>
          </SheetHeader>
          <div className="mt-4">
            {renderFields(addForm, setAddForm, 'add-fdny')}
            <div className="flex gap-2 mt-5">
              <Button onClick={createPermit} disabled={saving} className="bg-primary text-primary-foreground" data-testid="submit-add-fdny-permit">{saving ? 'Saving...' : 'Add Permit'}</Button>
              <Button variant="outline" onClick={() => setAddOpen(false)}>Cancel</Button>
            </div>
          </div>
        </SheetContent>
      </Sheet>

      <LinkExistingDialog
        open={linkOpen}
        onOpenChange={setLinkOpen}
        title="Link Existing FDNY Permits"
        description="Select FDNY permits recorded on this property to also track under this project."
        records={linkCandidates}
        renderPrimary={(p) => p.permit_type || 'Permit'}
        renderSecondary={(p) => `${p.permit_number || 'No permit #'}${p.status ? ` · ${p.status}` : ''}`}
        linking={linking}
        onConfirm={linkSelected}
        emptyText="No unlinked FDNY permits found for this property."
        testid="link-existing-fdny"
      />
    </div>
  );
}
